import React from "react";
import PropTypes from "prop-types";
import { Form as FormikForm, InputField, CheckboxField, AutocompleteField } from "components/common_components/Formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";
import Card from "components/common_components/Card";

function FullAddressUpdater({ form }) {
    const { address, address2, city, state, zipCode } = form.values.userAttorney || {};
    React.useEffect(() => {
        const parts = [address, address2, city, state, zipCode].filter(Boolean);
        form.setFieldValue("userAttorney.fullAddress", parts.join(", "));
    }, [address, address2, city, state, zipCode]);
    return null;
}

function Form({ data, resources, mode, onEdit, ...rest }) {
    const readOnly = mode === "detail";
    const { modules } = resources;

    const initialValues = { ...data, modules: data.modules.reduce((acc, m) => ({ ...acc, [m.id]: true }), {}) };

    const validationSchema = Yup.object({
        name: Yup.string().required("Required field"),
        phoneNumber: Yup.string().required("Required field"),
        email: Yup.string().required("Required field").email("Incorrect email"),
        password:
            mode === "add"
                ? Yup.string().required("Required field").min(6, "At least 6 characters")
                : Yup.string().notRequired().nullable(),
        isActive: Yup.string().required("Required field"),
        hasRestrictedAccess: Yup.string().required("Required field"),
    });

    return (
        <FormikForm validateOnChange initialValues={initialValues} validationSchema={validationSchema} showButtons={false} {...rest}>
            {(form) => (
                <>
                    <Grid container spacing={3} justifyContent="center">
                        <Grid item sm={12} md={12} lg={10}>
                            <Card title="General information">
                                <InputField disabled={readOnly} fieldName="name" label="Name *" />
                                <InputField disabled={readOnly} fieldName="phoneNumber" label="Phone number *" />
                                <InputField
                                    disabled={mode !== "add"}
                                    fieldName="email"
                                    label="Email *"
                                    helperText="e.g myname@domain.com"
                                    lowercase
                                />
                                {mode === "add" && (
                                    <InputField
                                        type="password"
                                        fieldName="password"
                                        label="Password *"
                                        helperText="It must have at least 6 characters"
                                    />
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} justifyContent="center">
                        <Grid item sm={12} md={12} lg={10}>
                            <Card title="Basic access">
                                <CheckboxField readOnly={readOnly} fieldName="isActive" label="Is active" />
                                <CheckboxField readOnly={readOnly} fieldName="hasRestrictedAccess" label="Has restricted access" />
                                <CheckboxField readOnly={readOnly} fieldName="canChatWithClients" label="Can chat with clients" />
                            </Card>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} justifyContent="center">
                        <Grid item sm={12} md={12} lg={10}>
                            <Card title="Module access">
                                {modules.map(({ id, name }, index) => (
                                    <CheckboxField key={index} readOnly={readOnly} fieldName={`modules.${id}`} label={name} />
                                ))}
                            </Card>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} justifyContent="center">
                        <Grid item sm={12} md={12} lg={10}>
                            <Card title="Groups">
                                <AutocompleteField
                                    disabled={readOnly}
                                    fieldName="groups"
                                    label=""
                                    options={resources.groups}
                                    multiple
                                    limitTags={50}
                                    valueKey="id"
                                    labelKey="name"
                                    variant="standard"
                                    renderOption={(option) => (option ? option.name : "")}
                                    getOptionLabel={(option) => (option ? option.name : "")}
                                    getOptionSelected={(opt, val) => opt.id === val.id}
                                />
                            </Card>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} justifyContent="center">
                        <Grid item sm={12} md={12} lg={10}>
                            <Card title="Attorney information">
                                <FullAddressUpdater form={form} />
                                <CheckboxField readOnly={readOnly} fieldName="isAttorney" label="Is attorney" />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.lastName"
                                    label="Last name"
                                    maxLength={50}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.firstName"
                                    label="First name"
                                    maxLength={50}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.middleName"
                                    label="Middle name"
                                    maxLength={50}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.address"
                                    label="Address"
                                    maxLength={100}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.address2"
                                    label="Address 2"
                                    maxLength={100}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.city"
                                    label="City"
                                    maxLength={100}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.state"
                                    label="State"
                                    maxLength={100}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.zipCode"
                                    label="Zip code"
                                    maxLength={20}
                                />
                                <InputField disabled fieldName="userAttorney.fullAddress" label="Full address" maxLength={200} />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.email1"
                                    label="Email 1"
                                    maxLength={100}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.email2"
                                    label="Email 2"
                                    maxLength={100}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.phoneNumber"
                                    label="Phone number"
                                    maxLength={20}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.uscisAccount"
                                    label="USCIS account"
                                    maxLength={100}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.stateLicensed"
                                    label="State licensed"
                                    maxLength={100}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.licenseNumber"
                                    label="License number"
                                    maxLength={50}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.volagNumber"
                                    label="Volag number"
                                    maxLength={50}
                                />
                                <InputField
                                    disabled={readOnly || !form.values.isAttorney}
                                    fieldName="userAttorney.courtName"
                                    label="Court"
                                    maxLength={50}
                                />
                            </Card>
                        </Grid>
                    </Grid>
                    {readOnly ? (
                        <Grid container spacing={3} justifyContent="center">
                            <Grid item sm={12} md={12} lg={10}>
                                <div className="button-group">
                                    <Button secondary outline onClick={rest.onCancel}>
                                        <Icon name="arrow" size="md" color="secondary" />
                                        <span>Back</span>
                                    </Button>
                                    <Button secondary onClick={onEdit}>
                                        <Icon name="pencil" size="md" color="white" />
                                        <span>Edit</span>
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={3} justifyContent="center">
                            <Grid item sm={12} md={12} lg={10}>
                                <div className="button-group">
                                    <Button
                                        outline
                                        onClick={() => {
                                            form.resetForm();
                                            rest.onCancel(form);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button primary type="submit" loading={form.isSubmitting}>
                                        <Icon name="save" size="md" color="white" />
                                        <span>Save</span>
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    )}
                </>
            )}
        </FormikForm>
    );
}

Form.propTypes = {
    data: PropTypes.shape({
        name: PropTypes.string,
        phoneNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        password: PropTypes.string,
        isActive: PropTypes.bool,
        isAdmin: PropTypes.bool,
        hasRestrictedAccess: PropTypes.bool,
        isAuthorized: PropTypes.bool,
        modules: PropTypes.arrayOf(PropTypes.shape({})),
        canChatWithClients: PropTypes.bool,
        groups: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            }),
        ),
        isAttorney: PropTypes.bool,
        userAttorney: PropTypes.shape({
            lastName: PropTypes.string,
            firstName: PropTypes.string,
            middleName: PropTypes.string,
            address: PropTypes.string,
            address2: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
            fullAddress: PropTypes.string,
            email1: PropTypes.string,
            email2: PropTypes.string,
            phoneNumber: PropTypes.string,
            uscisAccount: PropTypes.string,
            stateLicenced: PropTypes.string,
            licenceNumber: PropTypes.string,
            volageNumber: PropTypes.string,
            courtName: PropTypes.string,
        }),
    }),
    resources: PropTypes.shape({
        modules: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            }),
        ),
        groups: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            }),
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
};

Form.defaultProps = {
    data: {
        name: "",
        phoneNumber: "",
        email: "",
        password: "",
        isActive: true,
        isAdmin: false,
        hasRestrictedAccess: false,
        isAuthorized: false,
        modules: [],
        canChatWithClients: false,
        groups: [],
        isAttorney: false,
        userAttorney: {
            lastName: "",
            firstName: "",
            middleName: "",
            address: "",
            address2: "",
            city: "",
            state: "",
            zipCode: "",
            fullAddress: "",
            email1: "",
            email2: "",
            phoneNumber: "",
            uscisAccount: "",
            stateLicenced: "",
            licenceNumber: "",
            volageNumber: "",
            courtName: "",
        },
    },
    resources: {
        modules: [],
        groups: [],
    },
    onEdit: () => {},
};

export default Form;
